// @flow

import React from 'react';
import { type MessageTranslator } from '@oneflowab/pomes';

import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import type { Location } from 'react-router';

import adminPage from '@app/hocs/admin-page';
import { ExtensionCategory, categorize } from '@app/components/extension/extension-category/extension-category';
import GeneratedToken from '@app/components/extension/generated-token';

import {
  getSettingsComponent,
  getExtensionFromPath,
} from './marketplace-settings';
import { MarketplaceCard } from './marketplace-card/marketplace-card';

import style from './marketplace.module.scss';

export type Props = {
  extensions: Array<Extension>,
  queryExtensions: QueryFunc,
  toggleExtensionState: Extension => void,
};

export class Extensions extends React.Component<Props> {
  componentDidMount() {
    const { queryExtensions } = this.props;

    queryExtensions();
  }

  renderCategories = () => {
    const { extensions } = this.props;
    const categorizedExtensions = categorize(extensions);

    return (
      <>
        {categorizedExtensions.map((category) => (
          <React.Fragment key={category.category}>
            <div className={style.Header}>
              <ExtensionCategory
                categoryReadable={category.title}
              />
            </div>
            <div className={style.ExtensionsContainer}>
              {this.renderExtensions(category.extensions)}
            </div>
          </React.Fragment>
        ))}
      </>
    );
  }

  renderExtensions = (extensions) => {
    const { toggleExtensionState } = this.props;

    const sortedExtensions = sortBy<Extension>(extensions, (extension: Extension) => [
      extension.name,
    ]);

    return sortedExtensions.map((extension) => (
      <React.Fragment key={extension.key}>
        <MarketplaceCard
          extension={extension}
          toggleExtensionState={toggleExtensionState}
          trackable={{
            name: 'Go To Marketplace Card',
            props: {
              location: 'marketplace',
              key: extension.key,
            },
          }}
        />
        <GeneratedToken
          extension={extension}
        />
      </React.Fragment>
    ));
  };

  render() {
    return (
      <div className={style.MainContainer}>
        {this.renderCategories()}
      </div>
    );
  }
}

type MapperProps = {
  location: Location,
  message: MessageTranslator,
  extensions: Array<Extension>,
  query: Query,
  toggleExtensionState: Extension => void,
  updateExtension: Extension => void,
  queryExtensions: () => void,
  getUpdateState: (extensionId: number) => UpdateState,
  resetUpdateState: (extensionId: number) => void,
  dateFormat: string,
};

const getModules = ({
  location,
  extensions,
  query,
  toggleExtensionState,
  updateExtension,
  getUpdateState,
  resetUpdateState,
  dateFormat,
}) => {
  if (!extensions) {
    return [];
  }
  const extension = getExtensionFromPath(extensions, location.pathname);
  const extensionId = get(extension, 'extensionId');

  return [{
    path: '/:extension',
    title: '',
    component: getSettingsComponent(extension, query.status === 'success'),
    extension,
    toggleExtensionState,
    updateExtension,
    updateState: getUpdateState(extensionId),
    resetUpdateState: () => resetUpdateState(extensionId),
    dateFormat,
  }];
};

export const propsMapper = ({ props }: { props: MapperProps }) => ({
  title: props.message({
    id: 'Marketplace',
    comment: 'Used as the page title of the marketplace page',
  }),
  modules: [getModules(props)],
  componentDidMount: () => {
    if (!props.location.pathname.endsWith('/marketplace')) {
      props.queryExtensions();
    }
  },
});

export default adminPage(propsMapper)(Extensions);
